'use client'

import './Calculator.css'

import type { RateTable } from '@/utils/financing'
import type { IncomeRange } from '@/views/microsite-pf/pf/pfMicrositeShared'
import type { ChangeEvent } from 'react'
import type { CalculatorProps, PersonalFinancingRates } from './types'
import { calculatorTD, CustomSlider, marks } from '@/constants/calculator'

import { useEffect, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { isEmpty } from 'lodash'

import useStore from '@/stores/Store'
import api from '@/utils/api'
import {
  calculateMonthlyInstallment,
  formatInstallment,
  parseRateTable,
  validateFinancingAmount
} from '@/utils/financing'

const CalculatorTD = ({ onIncomeRangeChange }: CalculatorProps) => {
  const [incomeRange, setIncomeRange] = useState('below5k')
  const [financingAmount, setFinancingAmount] = useState('')
  const [tenure, setTenure] = useState(1)
  const [monthlyPayment, setMonthlyPayment] = useState('0.00')
  const [isActive, setIsActive] = useState(false)
  const [amountError, setAmountError] = useState('')
  const [isCalculated, setIsCalculated] = useState(false)
  const [incomeLow, setIncomeLow] = useState<RateTable>({})
  const [incomeHigh, setIncomeHigh] = useState<RateTable>({})
  const { calculatorState, setCalculatorState } = useStore()

  // eslint-disable-next-line no-unused-vars -- used in commented-out JSX below
  const handleIncomeChange = (range: IncomeRange) => {
    setIncomeRange(range)
    setTenure(
      range === 'below5k'
        ? Number.parseInt(Object.keys(incomeLow)[0])
        : Number.parseInt(Object.keys(incomeHigh)[0])
    )
    setFinancingAmount('')
    setMonthlyPayment('0.00')
    setAmountError('')
    onIncomeRangeChange?.(range)
  }

  const handleFinancingAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replaceAll(/[^0-9.]/g, '')
    setFinancingAmount(value)
    setAmountError('')
  }

  const calculateTotal = () => {
    const result = validateFinancingAmount(financingAmount, {
      min: incomeRange === 'below5k' ? 5000 : 5000,
      max: incomeRange === 'below5k' ? 500000 : 250000
    })
    if (result.status === 'required') {
      setAmountError(calculatorTD.amountErrorRequired)
      return
    }
    if (result.status === 'out-of-range') {
      setAmountError(
        incomeRange === 'below5k'
          ? calculatorTD.amountErrorRangeBelow5k
          : calculatorTD.amountErrorRangeAbove5k
      )
      return
    }
    const amount = result.amount

    // const repaymentRates =
    //   incomeRange === "below5k"
    //     ? {
    //         1: 4.21 / 100,
    //         2: 4.25 / 100,
    //         3: 4.32 / 100,
    //         4: 4.41 / 100,
    //         5: 4.89 / 100,
    //         6: 4.98 / 100,
    //         7: 5.33 / 100,
    //       }
    //     : {
    //         1: 4.21 / 100,
    //         2: 4.25 / 100,
    //         3: 4.32 / 100,
    //         4: 4.41 / 100,
    //         5: 4.89 / 100,
    //         6: 4.98 / 100,
    //         7: 5.33 / 100,
    //       };
    const repaymentRates = incomeRange === 'below5k' ? incomeLow : incomeHigh

    const rate = repaymentRates[tenure]
    const total = calculateMonthlyInstallment(amount, rate, tenure)

    setMonthlyPayment(formatInstallment(total))

    setFinancingAmount(amount.toString())
    setIsCalculated(true)
    setAmountError('')
  }

  useEffect(() => {
    if (isCalculated) {
      calculateTotal()
    }
  }, [tenure])

  const handleAssign = (data: PersonalFinancingRates) => {
    const incomeLowData = data.PersonalFinancingITdI?.[0]?.incomeLowRange
    const incomeHighData = data.PersonalFinancingITdI?.[0]?.incomeHighRange

    const incomeLowObject = parseRateTable(incomeLowData)
    const incomeHighObject = parseRateTable(incomeHighData)

    setIncomeLow(incomeLowObject)
    setIncomeHigh(incomeHighObject)
    if (!isEmpty(incomeLowObject) && !isEmpty(incomeHighObject)) {
      setTenure(
        incomeRange === 'below5k'
          ? Number.parseInt(Object.keys(incomeLowObject)[0])
          : Number.parseInt(Object.keys(incomeHighObject)[0])
      )
    }
  }

  const fetchPFITDData = async () => {
    try {
      const response = await api.get('/personal-financings?pLevel')
      setCalculatorState(response.data.data[0])

      handleAssign(response.data.data[0])
    } catch (error) {
      console.error('Error fetching data', error)
    }
  }

  // DELIBERATE CLIENT REFETCH (derived 2026-08-17 from Task 5) — do not remove.
  // Profit rates drive a figure the customer acts on; publish-to-deploy latency
  // (3-15 min) is too long to compute against a superseded rate. The host routes
  // are excluded from tests/no-client-cms-fetch.spec.ts. See Task 9 of
  // docs/superpowers/plans/2026-08-17-remove-client-side-cms-refetch.md
  useEffect(() => {
    if (isEmpty(calculatorState)) {
      fetchPFITDData()
    } else {
      handleAssign(calculatorState)
    }
  }, [])

  return (
    <div id="calculator" className="calculator section-calc light-background calculator-form">
      <div data-aos="fade-up" data-aos-delay="200" className="w-100">
        <p className="header">{calculatorTD.header}</p>
        {/* <div
          className="btn-group w-100"
          role="group"
          aria-label="Basic radio toggle button group"
        >
          <input
            type="radio"
            className="btn-check"
            name="btnradio"
            id="btnradio1"
            checked={incomeRange === "below5k"}
            onChange={() => handleIncomeChange("below5k")}
          />
          <label
            className="income btn btn-outline-primary"
            htmlFor="btnradio1"
            onClick={() => handleIncomeChange("below5k")}
          >
            {calculatorTD.incomeBelow5k}
          </label>

          <input
            type="radio"
            className="btn-check"
            name="btnradio"
            id="btnradio2"
            checked={incomeRange === "above5k"}
            onChange={() => handleIncomeChange("above5k")}
          />
          <label
            className="income btn btn-outline-primary"
            htmlFor="btnradio2"
            onClick={() => handleIncomeChange("above5k")}
          >
            {calculatorTD.incomeAbove5k}
          </label>
        </div> */}

        <div className={`form-group-calc ${isActive ? 'active' : ''}`}>
          <label htmlFor="financing-amount" className="income-group income-group-text ">
            {incomeRange === 'below5k'
              ? calculatorTD.financingAmountBelow5k
              : calculatorTD.financingAmountAbove5k}
          </label>
          <div className="input-group">
            <span className="amount">RM</span>
            <input
              type="text"
              id="financing-amount"
              name="financing-amount"
              placeholder={calculatorTD.insertAmountPlaceholder}
              className={`form-control-input ${amountError ? 'input-error' : ''}`}
              value={financingAmount}
              onChange={handleFinancingAmountChange}
              onFocus={() => setIsActive(true)}
              onBlur={() => setIsActive(false)}
              style={{ border: 'none' }}
            />
          </div>
          {amountError && <div className="error-message">{amountError}</div>}
        </div>

        <div className="form-group-2">
          <label htmlFor="tenure" className="tenure">
            {calculatorTD.tenureLabel}
          </label>
          <div className="input-group">
            <CustomSlider
              value={tenure}
              onChange={(_: unknown, value: number) => {
                setTenure(value)
                setIsCalculated(true)
              }}
              step={1}
              marks={marks}
              min={
                incomeRange === 'below5k'
                  ? Number.parseInt(Object.keys(incomeLow)[0])
                  : Number.parseInt(Object.keys(incomeHigh)[0])
              }
              max={
                incomeRange === 'below5k'
                  ? Number.parseInt(Object.keys(incomeLow)[Object.keys(incomeLow).length - 1])
                  : Number.parseInt(Object.keys(incomeHigh)[Object.keys(incomeHigh).length - 1])
              }
              valueLabelDisplay="off"
              aria-label="Financing Tenure"
            />
            {/* <CustomSlider
              value={tenure}
              onChange={(_: unknown, value: number) => {
                setTenure(value);
                setIsCalculated(true);
              }}
              step={1}
              marks={marks}
              min={1}
              max={7}
              valueLabelDisplay="off"
              aria-label="Financing Tenure"
            /> */}
          </div>
          <p className="payment">{calculatorTD.monthlyPaymentLabel}</p>

          <Container>
            <Row className="align-items-center justify-content-between row-cols-2">
              <Col xs={8} className="ps-0">
                <div className="form-group-3">
                  <div className="input-group center-input-group">
                    <span className="input-group-text-2">RM</span>
                    <span className="financing-amount-style">{monthlyPayment}</span>
                  </div>
                </div>
              </Col>
              <Col xs={4} className="pe-0">
                <button
                  type="button"
                  className="btn btn-primary calculate-button w-100"
                  onClick={calculateTotal}
                >
                  {calculatorTD.calculateTotalButton}
                </button>
              </Col>
            </Row>
          </Container>
        </div>
        <span className="notes">{calculatorTD.notes}</span>
      </div>
    </div>
  )
}

export default CalculatorTD
